import React, { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { copyToClipboard, createURLWithQueryParams } from "utils/helpers";
import { v4 as uuidv4 } from "uuid";
import { runBacktest, runBacktestHealth } from "./runBacktest";
import socket from "utils/socket";

import {
  Divider,
  Row,
  Form,
  Col,
  InputNumber,
  Select,
  App,
  Switch,
  Button,
  Popover,
  Progress,
} from "antd";
import {
  ShareAltOutlined,
  WhatsAppOutlined,
  LinkOutlined,
  ExportOutlined,
} from "@ant-design/icons";
import { apiCreateNewStrategy } from "api/apiStrategies";
import GenericCard from "components/UI/GenericCard/GenericCard";
import TVChartContainer from "components/TVChartContainer/TVChartContainer";
import AiBacktestTable from "components/AiMagician/AiBacktestTable";
import AccProfitChart from "./Charts/AccProfitChart";
import CreateStrategy from "components/Strategies/Modals/CreateStrategy";
import BacktestPerformance from "./BacktestPerformance";
import StrategyOpenOrders from "components/Strategies/Cards/StrategyOpenOrders";
import StrategySuccessRateChart from "./Charts/StrategySuccessRateChart";
import BacktestHealthPanel from "./BacktestHealth/BacktestHealthPanel";
import KeyMetrics from "./BacktestHealth/KeyMetrics";
import RangeSlider from "./RangeSlider";
import ChooseModelsForStrategy from "./ChooseModelsForStrategy";
import MonteCarloSimulation from "./MonteCarloSimulation";
import ProfitHistogram from "./ProfitHistogram";
// import PositionMinValueSuccessRate from "./Charts/PositionMinValueSuccessRate";
// import DaysOpenSuccessRateRelation from "./Charts/DaysOpenSuccessRateRelation";

const { Option } = Select;

const AiBacktestSection = (props) => {
  const { results, setStrategyResults, modelsList, handleSelectModel } = props;
  const { message } = App.useApp();
  const location = useLocation();

  const userPremiumFee = useSelector(
    (state) => state.main.user.preferences.premiumFee,
  );
  const [premiumFee, setPremiumFee] = useState(userPremiumFee);
  const [premiumPerShare, setPremiumPerShare] = useState(0);
  const [slippage, setSlippage] = useState(0);
  const [takeProfitPercent, setTakeProfitPercent] = useState(
    ((results.model.percToGain - 1) * 100).toFixed(2),
  );
  const [stopLossPercent, setStopLossPercent] = useState(0);
  // const [trailingStopLossPercent, setTrailingStopLossPercent] = useState(0);
  const [enterPositionBuyCount, setEnterPositionBuyCount] = useState(1);
  const [exitPositionNoBuyCount, setExitPositionNoBuyCount] = useState(0);
  const [daysOpenExitCount, setDaysOpenExitCount] = useState(
    results.model.days,
  );
  const [maxOpenOrders, setMaxOpenOrders] = useState(1);
  const [resetTrendAfterSell, setResetTrendAfterSell] = useState(true);

  const [initialBalance, setInitialBalance] = useState(1000);
  const [orderSize, setOrderSize] = useState(100);
  const [orderSizeType, setOrderSizeType] = useState("percent");

  // Intraday (1m) exits -- no UI to enable them right now, so they stay off.
  const [intradayEnabled, setIntradayEnabled] = useState(false);
  const [regularSessionOnly, setRegularSessionOnly] = useState(true);
  const [intradayTakeProfitEnabled, setIntradayTakeProfitEnabled] =
    useState(false);
  const [intradayTakeProfitThreshold, setIntradayTakeProfitThreshold] =
    useState(+takeProfitPercent || 0);
  const [intradayStopLossEnabled, setIntradayStopLossEnabled] = useState(false);
  const [intradayStopLossThreshold, setIntradayStopLossThreshold] = useState(
    +stopLossPercent || 0,
  );
  const [trailingTakeProfitEnabled, setTrailingTakeProfitEnabled] =
    useState(false);
  const [trailingActivationThreshold, setTrailingActivationThreshold] =
    useState(6.5);
  const [trailingDeclineFromPeakLimit, setTrailingDeclineFromPeakLimit] =
    useState(-1.5);
  const [isComputing, setIsComputing] = useState(false);
  const [computeProgress, setComputeProgress] = useState(0);
  const [health, setHealth] = useState(null);
  const [isHealthLoading, setIsHealthLoading] = useState(false);
  const [healthError, setHealthError] = useState(null);

  const [visibleRange, setVisibleRange] = useState({ from: 0, to: 0 });

  const [backtestResults, setBacktestResults] = useState();
  const shareUrl = useRef("");
  const walkForwardUrl = useRef("");
  const queryParams = new URLSearchParams(location.search);
  const isShared = queryParams.get("shared");
  const [startDate, setStartDate] = useState(
    +new Date(results.predictionsTable[0].date),
  );
  const [endDate, setEndDate] = useState(
    +new Date(
      results.predictionsTable[results.predictionsTable.length - 1].date,
    ),
  );

  const handleChangeTradingRange = (dates) => {
    setStartDate(dates[0]);
    setEndDate(dates[1]);
  };

  const createShareUrl = (params, startDate, endDate) => {
    const url = createURLWithQueryParams(window.location.href, {
      shared: true,
      ...params,
      startDate: dayjs(startDate).format("YYYY-MM-DD"),
      endDate: dayjs(endDate).format("YYYY-MM-DD"),
      modelId: results.selectedModels.map((modelId) => modelId).join(", "),
    });
    shareUrl.current = url;

    const url2 = createURLWithQueryParams(window.location.href, {
      shared: true,
      ...params,
      startDate: dayjs(endDate).format("YYYY-MM-DD"),
      endDate: dayjs().add(1, "day").format("YYYY-MM-DD"),
      modelId: results.selectedModels.map((modelId) => modelId).join(", "),
    });
    walkForwardUrl.current = url2;
  };

  useEffect(() => {
    const backtestSettings = {
      takeProfitPercent,
      enterPositionBuyCount,
      stopLossPercent,
      // trailingStopLossPercent,
      exitPositionNoBuyCount,
      daysOpenExitCount,
      initialBalance,
      orderSize,
      orderSizeType,
      maxOpenOrders,
      resetTrendAfterSell,

      symbol: results.symbol,
      modelId: results.model._id,
      userCommission: premiumFee,
      premiumPerShare,
      slippage,
      startDate,
      endDate,
    };

    createShareUrl(
      backtestSettings,
      results.predictionsTable[0].date,
      results.predictionsTable[results.predictionsTable.length - 1].date,
    );

    backtestSettings.predictionsTable = results.predictionsTable;

    const intraday = {
      enabled: intradayEnabled,
      regularSessionOnly,
      takeProfit: {
        enabled: intradayTakeProfitEnabled,
        threshold: +intradayTakeProfitThreshold,
      },
      stopLoss: {
        enabled: intradayStopLossEnabled,
        threshold: +intradayStopLossThreshold,
      },
      trailingTakeProfit: {
        enabled: trailingTakeProfitEnabled,
        activationThreshold: +trailingActivationThreshold,
        declineFromPeakLimit: +trailingDeclineFromPeakLimit,
      },
    };

    const abortController = new AbortController();
    const uuid = uuidv4();
    let isCancelled = false;

    const handleProgress = (socketData) => {
      if (typeof socketData?.progress === "number") {
        setComputeProgress(socketData.progress);
      }
    };

    const runOpts = {
      models: results.selectedModels,
      from: results.startDate,
      to: results.endDate,
      signal: abortController.signal,
    };
    const isAborted = (error) =>
      error?.name === "CanceledError" || error?.code === "ERR_CANCELED";

    // robustness checks run in parallel with the backtest; daily exits only
    const computeHealth = async () => {
      setIsHealthLoading(true);
      setHealthError(null);
      try {
        const healthData = await runBacktestHealth(backtestSettings, runOpts);
        if (!isCancelled) setHealth(healthData);
      } catch (error) {
        if (!isAborted(error) && !isCancelled) {
          setHealthError("Failed to run the robustness checks");
        }
      } finally {
        if (!isCancelled) setIsHealthLoading(false);
      }
    };

    const compute = async () => {
      socket.on(uuid, handleProgress);
      setIsComputing(true);
      setComputeProgress(0);
      computeHealth();

      try {
        const backtestResultsData = await runBacktest(backtestSettings, {
          ...runOpts,
          intraday,
          uuid,
        });

        if (!isCancelled) {
          setBacktestResults(backtestResultsData);
        }
      } catch (error) {
        if (!isAborted(error) && !isCancelled) {
          message.error("Failed to compute backtest");
        }
      } finally {
        socket.off(uuid, handleProgress);
        if (!isCancelled) {
          setIsComputing(false);
        }
      }
    };

    // debounce -- this effect fires on every keystroke (e.g. typing into Take
    // Profit), and each run is a server request
    const debounceTimer = setTimeout(compute, 300);

    return () => {
      isCancelled = true;
      clearTimeout(debounceTimer);
      abortController.abort();
      socket.off(uuid, handleProgress);
    };
  }, [
    premiumFee,
    premiumPerShare,
    takeProfitPercent,
    enterPositionBuyCount,
    stopLossPercent,
    // trailingStopLossPercent,
    exitPositionNoBuyCount,
    daysOpenExitCount,
    initialBalance,
    orderSize,
    orderSizeType,
    results,
    maxOpenOrders,
    slippage,
    resetTrendAfterSell,
    startDate,
    endDate,
    intradayEnabled,
    regularSessionOnly,
    intradayTakeProfitEnabled,
    intradayTakeProfitThreshold,
    intradayStopLossEnabled,
    intradayStopLossThreshold,
    trailingTakeProfitEnabled,
    trailingActivationThreshold,
    trailingDeclineFromPeakLimit,
  ]);

  const resetVisibleRange = () => {
    setVisibleRange({ from: 0, to: 0 });
  };

  const setTransactionVisibleRange = (openDate, closeDate) => {
    const from = Math.floor(+new Date(openDate)) / 1000;
    const to = Math.floor(+new Date(closeDate)) / 1000;
    setVisibleRange({ from, to });
  };

  // all open positions together can use at most 100% of equity
  const isOrderSizingTooLarge =
    orderSizeType === "percent" && (maxOpenOrders || 1) * orderSize > 100;

  const createNewStrategy = async (values) => {
    if (isOrderSizingTooLarge) {
      message.error("Max open orders × order size exceeds 100% of equity");
      return;
    }
    try {
      const strategy = {
        symbol: results.symbol,
        models: results.selectedModels,
        resolution: results.model.resolution,

        params: {
          takeProfitPercent,
          stopLossPercent,
          enterPositionBuyCount,
          exitPositionNoBuyCount,
          daysOpenExitCount,
          orderSizeType,
          orderSize,
          maxOpenOrders: maxOpenOrders || 1,
        },

        backtestStartDate: dayjs(startDate).format("YYYY-MM-DD"),

        initialBalance,
        balance: initialBalance,
        equity: initialBalance,
        ...values,
      };
      if (values.automated && !values.isDemo) {
        strategy.automationSettings = {
          buyMaxPercentDiff: values.buyMaxPercentDiff,
          orderUpdateInterval: values.orderUpdateInterval,
          trailingTakeProfit: {
            enabled: values.trailingTakeProfitEnabled || false,
            activationThreshold: values.trailingTakeProfitActivationThreshold,
            declineFromPeakLimit: values.trailingTakeProfitDeclineFromPeakLimit,
          },
          intradayTakeProfit: {
            enabled: values.intradayTakeProfitEnabled || false,
            threshold: values.intradayTakeProfitThreshold,
          },
          intradayStopLoss: {
            enabled: values.intradayStopLossEnabled || false,
            threshold: values.intradayStopLossThreshold,
          },
        };
      }

      await apiCreateNewStrategy(strategy);
      message.success("Strategy created successfully");
    } catch (error) {
      message.error(
        error?.response?.data?.message || "Failed to create strategy",
      );
    }
  };

  const onCopyBacktestLinkToClipboard = () => {
    copyToClipboard(shareUrl.current);
    message.success("Copied share URL to clipboard");
  };

  const shareToWhatsapp = () => {
    const encodedMessage = encodeURIComponent(shareUrl.current);
    window.open(
      `https://api.whatsapp.com/send?text=${encodedMessage}`,
      "_blank",
    );
  };

  useEffect(() => {
    if (backtestResults) {
      setStrategyResults((prevState) => {
        return { ...prevState, [backtestResults._id]: backtestResults };
      });
    }

    return () => {
      if (backtestResults) {
        setStrategyResults((prevState) => {
          delete prevState[backtestResults._id];
          return prevState;
        });
      }
    };
  }, [backtestResults]);

  useEffect(() => {
    // set backtest params from share url
    if (isShared) {
      setTakeProfitPercent(+queryParams.get("takeProfitPercent"));
      setEnterPositionBuyCount(+queryParams.get("enterPositionBuyCount"));
      setStopLossPercent(+queryParams.get("stopLossPercent"));
      setExitPositionNoBuyCount(+queryParams.get("exitPositionNoBuyCount"));
      setDaysOpenExitCount(+queryParams.get("daysOpenExitCount"));
      setInitialBalance(+queryParams.get("initialBalance"));
      setOrderSize(+queryParams.get("orderSize"));
      setOrderSizeType(queryParams.get("orderSizeType"));
      setMaxOpenOrders(+queryParams.get("maxOpenOrders") || 1);
      setPremiumFee(+queryParams.get("userCommission"));

      setResetTrendAfterSell(
        queryParams.get("resetTrendAfterSell") !== "false",
      );
    }
  }, []);

  useEffect(() => {
    // set backtest params from a backtest link added with the "+" tab
    if (results?.backtestParams && !isShared) {
      const params = results.backtestParams;
      setTakeProfitPercent(params.takeProfitPercent);
      setEnterPositionBuyCount(params.enterPositionBuyCount);
      setStopLossPercent(params.stopLossPercent);
      setExitPositionNoBuyCount(params.exitPositionNoBuyCount);
      setDaysOpenExitCount(params.daysOpenExitCount);
      setInitialBalance(params.initialBalance);
      setOrderSize(params.orderSize);
      setOrderSizeType(params.orderSizeType);
      setPremiumFee(+params.userCommission);
      setMaxOpenOrders(params.maxOpenOrders);
      setResetTrendAfterSell(params.resetTrendAfterSell);
      setPremiumPerShare(params.premiumPerShare);
    }
  }, [results]);

  return (
    <>
      <Form layout="vertical" name="overview">
        <GenericCard>
          {results.predictionsTable && (
            <RangeSlider
              predictionsTable={results.predictionsTable}
              handleChangeTradingRange={handleChangeTradingRange}
              startDate={startDate}
              endDate={endDate}
            />
          )}
          <Row gutter={32} align="start">
            <Col xs={12}>
              <Divider orientation="left">Strategy properties</Divider>

              <Row gutter={8}>
                <Col>
                  <Form.Item label="Inital balance">
                    <InputNumber
                      min={0}
                      prefix="$"
                      style={{ width: "100%" }}
                      value={initialBalance}
                      onChange={setInitialBalance}
                    />
                  </Form.Item>
                </Col>
                <Col>
                  <Form.Item
                    label="Order size"
                    tooltip={
                      <div>
                        <p>
                          <strong>USD:</strong> Fixed amount every order
                        </p>
                        <p>
                          <strong>% of Equity:</strong> Each order is % of
                          equity
                        </p>
                        {/* <p>
                      <strong>% of A. Balance:</strong> Each order is % of
                      available strategy balance
                    </p> */}
                      </div>
                    }
                  >
                    <InputNumber
                      min={0}
                      max={orderSizeType === "percent" ? 100 : undefined}
                      addonAfter={
                        <Form.Item noStyle>
                          <Select
                            style={{
                              width: 115,
                            }}
                            value={orderSizeType}
                            onChange={setOrderSizeType}
                          >
                            <Option value="USD">USD</Option>
                            <Option value="percent">% of equity</Option>
                          </Select>
                        </Form.Item>
                      }
                      style={{ width: "100%" }}
                      value={orderSize}
                      onChange={setOrderSize}
                    />
                  </Form.Item>
                </Col>
                <Col>
                  <Form.Item
                    label="Max open orders"
                    tooltip="Positions the strategy may hold at the same time"
                    validateStatus={isOrderSizingTooLarge ? "error" : undefined}
                    help={
                      isOrderSizingTooLarge
                        ? "Max open orders × order size exceeds 100% of equity"
                        : undefined
                    }
                  >
                    <InputNumber
                      min={1}
                      precision={0}
                      style={{ width: "100%" }}
                      value={maxOpenOrders}
                      onChange={setMaxOpenOrders}
                    />
                  </Form.Item>
                </Col>
                <Col>
                  <Form.Item
                    label="Slippage"
                    tooltip={
                      <p>
                        The difference between the expected price of a trade and
                        the price at which the trade is executed
                      </p>
                    }
                  >
                    <InputNumber
                      step={0.01}
                      style={{ width: "100%" }}
                      value={slippage}
                      onChange={setSlippage}
                    />
                  </Form.Item>
                </Col>
                <Col>
                  <Form.Item
                    label="Commission per share"
                    tooltip={
                      <p>
                        The commission paid to the Broker per share to open the
                        trade
                      </p>
                    }
                  >
                    <InputNumber
                      min={0}
                      prefix="$"
                      step={0.01}
                      style={{ width: "100%" }}
                      value={premiumPerShare}
                      onChange={setPremiumPerShare}
                    />
                  </Form.Item>
                </Col>
                <Col>
                  <Form.Item
                    label="Min Commission"
                    tooltip={
                      <p>
                        The minimum commission amount paid to the Broker to open
                        the trade
                      </p>
                    }
                  >
                    <InputNumber
                      min={0}
                      prefix="$"
                      style={{ width: "100%" }}
                      value={premiumFee}
                      onChange={setPremiumFee}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
            <Col sm={24} lg={12}>
              <Row gutter={16}>
                <Divider orientation="left">Supported only in backtest</Divider>
                <Col>
                  <Form.Item label="Reset prediction trend after sell">
                    <Switch
                      checked={resetTrendAfterSell}
                      onChange={setResetTrendAfterSell}
                    />
                  </Form.Item>
                </Col>
                {/* <Col>
                  <Form.Item
                    label="Intraday (1m) exits"
                    tooltip="Check take profit / stop loss / trailing take profit minute-by-minute against 1m candles instead of only at the daily close. Requires the symbol to have 1m data, and always runs on the server."
                  >
                    <Switch
                      checked={intradayEnabled}
                      onChange={(checked) => {
                        setIntradayEnabled(checked);
                        // trailing take profit only exists intraday -- don't
                        // leave it silently enabled once its only UI is hidden
                        if (!checked) setTrailingTakeProfitEnabled(false);
                      }}
                    />
                  </Form.Item>
                </Col> */}
                {isComputing && (
                  <Col flex="auto" style={{ minWidth: 200 }}>
                    <Form.Item label="Computing...">
                      <Progress percent={computeProgress} size="small" />
                    </Form.Item>
                  </Col>
                )}
              </Row>
            </Col>
          </Row>
          <Row gutter={32}>
            <Col xs={24} lg={6}>
              <Divider orientation="left">Deal Enter rules</Divider>
              <Row gutter={8} align="bottom">
                <Col>
                  <Form.Item label="Buy predict count">
                    <InputNumber
                      min={1}
                      style={{ width: "100%" }}
                      value={enterPositionBuyCount}
                      onChange={setEnterPositionBuyCount}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Col>

            <Col xs={24} lg={18}>
              <Divider orientation="left">Deal close rules</Divider>
              <Row gutter={16} align="bottom" justify="start">
                <Col>
                  <Form.Item label="Take Profit">
                    <InputNumber
                      min={0}
                      prefix="%"
                      style={{ width: "100%" }}
                      value={takeProfitPercent}
                      onChange={setTakeProfitPercent}
                    />
                  </Form.Item>
                </Col>
                <Col>
                  <Form.Item label="Stop Loss">
                    <InputNumber
                      max={0}
                      prefix="%"
                      style={{ width: "100%" }}
                      value={stopLossPercent}
                      onChange={setStopLossPercent}
                    />
                  </Form.Item>
                </Col>
                {/* <Col>
                  <Form.Item label="Trailing Stop Loss">
                    <InputNumber
                      max={0}
                      prefix="%"
                      style={{ width: "100%" }}
                      value={trailingStopLossPercent}
                      onChange={setTrailingStopLossPercent}
                    />
                  </Form.Item>
                </Col> */}
                <Col>
                  <Form.Item label="No buy count">
                    <InputNumber
                      min={0}
                      style={{ width: "100%" }}
                      value={exitPositionNoBuyCount}
                      onChange={setExitPositionNoBuyCount}
                    />
                  </Form.Item>
                </Col>
                <Col>
                  <Form.Item label="Bars Open Count">
                    <InputNumber
                      min={0}
                      style={{ width: "100%" }}
                      value={daysOpenExitCount}
                      onChange={setDaysOpenExitCount}
                    />
                  </Form.Item>
                </Col>
              </Row>
              {intradayEnabled && (
                <Row gutter={16} align="bottom" justify="start">
                  <Col>
                    <Form.Item
                      label="Regular session only"
                      tooltip="Only evaluate intraday exits during 09:30-16:00 ET. Off also includes pre/post-market bars."
                    >
                      <Switch
                        checked={regularSessionOnly}
                        onChange={setRegularSessionOnly}
                      />
                    </Form.Item>
                  </Col>
                  <Col>
                    <Form.Item label="Intraday Take Profit">
                      <Switch
                        checked={intradayTakeProfitEnabled}
                        onChange={setIntradayTakeProfitEnabled}
                      />
                    </Form.Item>
                  </Col>
                  {intradayTakeProfitEnabled && (
                    <Col>
                      <Form.Item label="Threshold">
                        <InputNumber
                          min={0}
                          prefix="%"
                          style={{ width: "100%" }}
                          value={intradayTakeProfitThreshold}
                          onChange={setIntradayTakeProfitThreshold}
                        />
                      </Form.Item>
                    </Col>
                  )}
                  <Col>
                    <Form.Item label="Intraday Stop Loss">
                      <Switch
                        checked={intradayStopLossEnabled}
                        onChange={setIntradayStopLossEnabled}
                      />
                    </Form.Item>
                  </Col>
                  {intradayStopLossEnabled && (
                    <Col>
                      <Form.Item label="Threshold">
                        <InputNumber
                          max={0}
                          prefix="%"
                          style={{ width: "100%" }}
                          value={intradayStopLossThreshold}
                          onChange={setIntradayStopLossThreshold}
                        />
                      </Form.Item>
                    </Col>
                  )}
                  <Col>
                    <Form.Item
                      label="Trailing Take Profit"
                      tooltip="1m-only: continuously tracks the highest price reached since entry, then closes once price declines the given % from that peak (after the peak first reaches the activation threshold)."
                    >
                      <Switch
                        checked={trailingTakeProfitEnabled}
                        onChange={setTrailingTakeProfitEnabled}
                      />
                    </Form.Item>
                  </Col>
                  {trailingTakeProfitEnabled && (
                    <>
                      <Col>
                        <Form.Item label="Activation threshold">
                          <InputNumber
                            min={0}
                            prefix="%"
                            style={{ width: "100%" }}
                            value={trailingActivationThreshold}
                            onChange={setTrailingActivationThreshold}
                          />
                        </Form.Item>
                      </Col>
                      <Col>
                        <Form.Item label="Decline from peak">
                          <InputNumber
                            max={0}
                            prefix="%"
                            style={{ width: "100%" }}
                            value={trailingDeclineFromPeakLimit}
                            onChange={setTrailingDeclineFromPeakLimit}
                          />
                        </Form.Item>
                      </Col>
                    </>
                  )}
                </Row>
              )}
            </Col>
          </Row>
          <Row gutter={32} align="bottom">
            <Col>
              <Form.Item label="Eclipse">
                <ChooseModelsForStrategy
                  modelsList={modelsList}
                  results={results}
                  handleSelectModel={handleSelectModel}
                />
              </Form.Item>
            </Col>
            {results.model.resolution === "1d" && (
              <Col>
                <Form.Item>
                  <CreateStrategy
                    createNewStrategy={createNewStrategy}
                    takeProfitPercent={+takeProfitPercent}
                    stopLossPercent={+stopLossPercent}
                    defaultTrailingTakeProfitEnabled={trailingTakeProfitEnabled}
                    defaultTrailingActivationThreshold={
                      +trailingActivationThreshold
                    }
                    defaultTrailingDeclineFromPeakLimit={
                      +trailingDeclineFromPeakLimit
                    }
                    defaultIntradayTakeProfitEnabled={
                      intradayEnabled && intradayTakeProfitEnabled
                    }
                    defaultIntradayTakeProfitThreshold={
                      +intradayTakeProfitThreshold
                    }
                    defaultIntradayStopLossEnabled={
                      intradayEnabled && intradayStopLossEnabled
                    }
                    defaultIntradayStopLossThreshold={
                      +intradayStopLossThreshold
                    }
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
        </GenericCard>
      </Form>
      <Divider orientation="left">Backtest results</Divider>
      {backtestResults && (
        <div className="cards-wrapper">
          <BacktestHealthPanel
            health={health}
            loading={isHealthLoading}
            error={healthError}
          />
          <KeyMetrics backtestResults={backtestResults} />
          <AccProfitChart strategy={backtestResults} />
          {backtestResults.openOrders &&
            backtestResults.openOrders.length > 0 && (
              <GenericCard
                title="Open Orders"
                className="table-card"
                maxHeight={610}
              >
                <StrategyOpenOrders
                  openOrders={backtestResults.openOrders}
                  profitTargetPercent={backtestResults.params.takeProfitPercent}
                />
              </GenericCard>
            )}

          <AiBacktestTable
            transactions={backtestResults.transactions}
            setTransactionVisibleRange={setTransactionVisibleRange}
          />
          <GenericCard
            title="Trading View"
            className="table-card"
            isCardOpenByDefault={false}
          >
            <TVChartContainer
              symbol={results?.symbol}
              transactions={backtestResults.transactions}
              openPositions={backtestResults.openOrders}
              visibleRange={visibleRange}
              resetVisibleRange={resetVisibleRange}
            />
          </GenericCard>
          <GenericCard
            title="Monte Carlo Simulation"
            maxHeight={1200}
            isCardOpenByDefault={false}
            className="table-card"
          >
            <MonteCarloSimulation backtestResults={backtestResults} />
          </GenericCard>
          <ProfitHistogram backtestResults={backtestResults} />
          {/* <PositionMinValueSuccessRate backtestResults={backtestResults} />
          <DaysOpenSuccessRateRelation backtestResults={backtestResults} /> */}
          <StrategySuccessRateChart backtestResults={backtestResults} />
          <BacktestPerformance backtestResults={backtestResults} />

          <Popover
            content={
              <div className="flex gap-2 flex-wrap">
                <Button
                  icon={<LinkOutlined />}
                  onClick={onCopyBacktestLinkToClipboard}
                >
                  Copy link
                </Button>
                <Button onClick={shareToWhatsapp} icon={<WhatsAppOutlined />}>
                  Whatsapp
                </Button>
                <a href={shareUrl.current} target="_blank" rel="noreferrer">
                  <Button icon={<ExportOutlined />}>New tab</Button>
                </a>
                <a
                  href={walkForwardUrl.current}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Button icon={<ExportOutlined />}>Walk Forward</Button>
                </a>
              </div>
            }
            title="Share this backtest with others"
          >
            <Button
              icon={<ShareAltOutlined />}
              onClick={onCopyBacktestLinkToClipboard}
            >
              Share
            </Button>
          </Popover>
        </div>
      )}
    </>
  );
};

AiBacktestSection.propTypes = {};

export default AiBacktestSection;
